import { createTwoFilesPatch } from 'diff';
import {
  isSensitiveDiagnosticKey,
  redactSensitiveDiagnosticText,
} from './sensitiveRedaction';
import {
  TEST_STATUS_AUTO_MERGED,
  TEST_STATUS_FAILED,
  TEST_STATUS_FLAGGED_FOR_REVIEW,
  TEST_STATUS_PASSED,
  TEST_STATUS_PENDING,
} from '../types/TestTypes';

export interface MarkdownTestRun {
  id: string;
  status: string;
  createdAt: string;
  actualOutput: any;
}

export interface MarkdownTestInvocation {
  id: string;
  realId: string;
  name: string;
  organisation_id: string;
  /** Absent for generic flow-definition testcases (QA-10 F10). */
  flow_id?: string | null;
  sourceFlowInvocation_id?: string;
  expectedOutput: any;
}

export interface MarkdownGroup {
  stepId?: string | null;
  stepName: any;
  stepType?: string;
  writes: any[];
  hasMismatch: boolean;
}

export interface MarkdownExportProps {
  testInvocation: MarkdownTestInvocation;
  lastRun: MarkdownTestRun | null;
  groups: MarkdownGroup[];
  expectedSummary: any;
  actualSummary: any;
  hasSummaryMismatch: boolean;
  isDiffUnavailable?: boolean;
  mismatchCount: number;
  parityStatus?: 'passed' | 'failed' | 'unavailable';
}

export interface MarkdownExportOptions {
  includeOnlyDiffs?: boolean;
}

const REDACTED_SECRET_VALUE = '{redacted}';
const MAX_MARKDOWN_GROUPS = 100;
const MAX_MARKDOWN_WRITES = 500;

export interface SnapshotExportRedactionOptions {
  isSensitiveKey?: (key: string) => boolean;
  redactText?: (text: string) => string;
  redactedValue?: string;
}

export function redactSnapshotExportText(value: string): string {
  return redactSensitiveDiagnosticText(value, REDACTED_SECRET_VALUE);
}

export function redactSnapshotExportValue(
  value: any,
  options: SnapshotExportRedactionOptions = {},
  seen: WeakSet<object> = new WeakSet(),
): any {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return (options.redactText || redactSnapshotExportText)(value);
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map(item => redactSnapshotExportValue(item, options, seen));
  }

  const redacted = Object.fromEntries(Object.entries(value).map(([key, entryValue]) => [
    key,
    (options.isSensitiveKey || isSensitiveDiagnosticKey)(key)
      ? (options.redactedValue || REDACTED_SECRET_VALUE)
      : redactSnapshotExportValue(entryValue, options, seen),
  ]));
  if (value.__snapshotDiffTruncated === true && value.type === 'circular') {
    redacted.type = 'unavailable';
  }
  return redacted;
}

export function getDiffDisplayStatus(isMismatched: boolean, runStatus?: string): { label: string; bsStyle: 'success' | 'danger' | 'warning' | 'default' } {
  if (!isMismatched) {
    return { label: TEST_STATUS_PASSED, bsStyle: 'success' };
  }

  switch (runStatus) {
    case TEST_STATUS_AUTO_MERGED:
      return { label: TEST_STATUS_AUTO_MERGED, bsStyle: 'warning' };
    case TEST_STATUS_FLAGGED_FOR_REVIEW:
      return { label: TEST_STATUS_FLAGGED_FOR_REVIEW, bsStyle: 'warning' };
    case TEST_STATUS_FAILED:
      return { label: TEST_STATUS_FAILED, bsStyle: 'danger' };
    case TEST_STATUS_PENDING:
      return { label: TEST_STATUS_PENDING, bsStyle: 'default' };
    case TEST_STATUS_PASSED:
      return { label: 'mismatch', bsStyle: 'danger' };
    default:
      return { label: isMismatched ? 'mismatch' : TEST_STATUS_PASSED, bsStyle: isMismatched ? 'danger' : 'success' };
  }
}

function stringifyMarkdownValue(value: any): string {
  return JSON.stringify(typeof value === 'undefined' ? null : value, null, 2);
}

function createMarkdownDiff(expected: any, actual: any, filename: string): string {
  return createTwoFilesPatch(filename, filename, stringifyMarkdownValue(expected), stringifyMarkdownValue(actual), 'Expected', 'Actual').trim();
}

export function areSnapshotExportValuesSemanticallyEqual(expected: any, actual: any, seen: Array<[any, any]> = []): boolean {
  if (expected === actual) {
    return true;
  }
  if (!expected || !actual || typeof expected !== 'object' || typeof actual !== 'object') {
    return Number.isNaN(expected) && Number.isNaN(actual);
  }
  if (Array.isArray(expected) || Array.isArray(actual)) {
    return Array.isArray(expected)
      && Array.isArray(actual)
      && expected.length === actual.length
      && expected.every((value, index) => areSnapshotExportValuesSemanticallyEqual(value, actual[index], seen));
  }
  if (seen.some(pair => pair[0] === expected && pair[1] === actual)) {
    return true;
  }

  const expectedKeys = Object.keys(expected).sort();
  const actualKeys = Object.keys(actual).sort();
  if (expectedKeys.length !== actualKeys.length
    || expectedKeys.some((key, index) => key !== actualKeys[index])) {
    return false;
  }

  const nextSeen = seen.concat([[expected, actual]] as Array<[any, any]>);
  return expectedKeys.every(key => areSnapshotExportValuesSemanticallyEqual(expected[key], actual[key], nextSeen));
}

function areMarkdownGroupsValid(groups: any): groups is MarkdownGroup[] {
  if (!Array.isArray(groups) || groups.length > MAX_MARKDOWN_GROUPS) {
    return false;
  }

  let writeCount = 0;
  for (const group of groups) {
    if (!group
      || typeof group !== 'object'
      || typeof group.stepName !== 'string'
      || typeof group.hasMismatch !== 'boolean'
      || !Array.isArray(group.writes)) {
      return false;
    }
    writeCount += group.writes.length;
    if (writeCount > MAX_MARKDOWN_WRITES
      || !group.writes.every((write: any) => write && typeof write === 'object' && typeof write.isMismatched === 'boolean')) {
      return false;
    }
  }
  return true;
}

export function buildSnapshotValidationMarkdown(
  props: MarkdownExportProps,
  options: MarkdownExportOptions = {},
): string {
  const {
    testInvocation,
    lastRun,
    groups,
    expectedSummary,
    actualSummary,
    hasSummaryMismatch,
    isDiffUnavailable = false,
    mismatchCount,
    parityStatus,
  } = props;
  const { includeOnlyDiffs = true } = options;
  const groupsAreValid = areMarkdownGroupsValid(groups);
  const effectiveDiffUnavailable = isDiffUnavailable || !groupsAreValid;
  const normalizedExpectedSummaryForComparison = typeof expectedSummary === 'undefined' ? null : expectedSummary;
  const normalizedActualSummaryForComparison = typeof actualSummary === 'undefined' ? null : actualSummary;
  const effectiveSummaryMismatch = hasSummaryMismatch
    && !areSnapshotExportValuesSemanticallyEqual(
      normalizedExpectedSummaryForComparison,
      normalizedActualSummaryForComparison,
    );
  const redactedTestInvocation = { name: redactSnapshotExportValue(testInvocation?.name) };
  const redactedLastRun = lastRun
    ? {
      createdAt: redactSnapshotExportValue(lastRun.createdAt),
      status: redactSnapshotExportValue(lastRun.status),
    }
    : null;
  const redactedGroups = groupsAreValid ? redactSnapshotExportValue(groups) as MarkdownGroup[] : [];
  const redactedExpectedSummary = redactSnapshotExportValue(expectedSummary);
  const redactedActualSummary = redactSnapshotExportValue(actualSummary);
  const normalizedExpectedSummary = typeof redactedExpectedSummary === 'undefined' ? null : redactedExpectedSummary;
  const normalizedActualSummary = typeof redactedActualSummary === 'undefined' ? null : redactedActualSummary;
  const lines: string[] = [];

  lines.push(`# Snapshot Validation Report`);
  lines.push('');
  lines.push(`**Test:** ${redactedTestInvocation.name}`);
  lines.push(`**Run At:** ${redactedLastRun ? redactedLastRun.createdAt : 'N/A'}`);
  lines.push(`**Status:** ${redactedLastRun ? redactedLastRun.status : 'Not run'}`);
  if (parityStatus) {
    lines.push(`**Parity Status:** ${parityStatus}`);
  }
  if (mismatchCount > 0) {
    lines.push(`**Mismatches:** ${mismatchCount}`);
  }
  if (includeOnlyDiffs) {
    lines.push(`**Export Mode:** Diffs only`);
  }
  lines.push('');

  if (!includeOnlyDiffs) {
    lines.push('## Expected Output');
    lines.push('```json');
    lines.push(stringifyMarkdownValue(redactSnapshotExportValue(testInvocation.expectedOutput)));
    lines.push('```');
    lines.push('');
    lines.push('## Actual Output');
    lines.push('```json');
    lines.push(stringifyMarkdownValue(redactSnapshotExportValue(lastRun ? lastRun.actualOutput : null)));
    lines.push('```');
    lines.push('');
    return lines.join('\n');
  }

  if (effectiveDiffUnavailable) {
    lines.push('## Diff Unavailable');
    lines.push('');
    lines.push('_The computed diff payload is unavailable or invalid for this run._');
    lines.push('');
    return lines.join('\n');
  }

  if (effectiveSummaryMismatch) {
    lines.push(`## Summary Mismatch`);
    lines.push('');
    if (areSnapshotExportValuesSemanticallyEqual(normalizedExpectedSummary, normalizedActualSummary)) {
      lines.push('_The summaries differ only in redacted or truncated values._');
    } else {
      lines.push('```diff');
      lines.push(createMarkdownDiff(normalizedExpectedSummary, normalizedActualSummary, 'summary.json'));
      lines.push('```');
    }
    lines.push('');
  }

  const exportGroups = redactedGroups.filter(group => group.writes.some(write => write.isMismatched));

  if (exportGroups.length > 0) {
    lines.push(`## Step Timeline`);
    lines.push('');

    exportGroups.forEach((group, idx) => {
      const writes = group.writes.filter(write => write.isMismatched);
      const groupStatus = getDiffDisplayStatus(group.hasMismatch || writes.length > 0, redactedLastRun ? redactedLastRun.status : undefined);

      lines.push(`### ${idx + 1}. ${group.stepName}${group.stepType ? ` (${group.stepType})` : ''} ${groupStatus.bsStyle === 'success' ? '✅' : '⚠️'} **${groupStatus.label}**`);
      lines.push('');

      writes.forEach((write, wIdx) => {
        const fetchLabel = write.fetchName || write.actionName || 'Unnamed Fetch';
        const writeStatus = getDiffDisplayStatus(write.isMismatched, redactedLastRun ? redactedLastRun.status : undefined);
        const writeIndex = typeof write.index === 'number' ? write.index + 1 : wIdx + 1;

        lines.push(`#### Write #${writeIndex}: ${fetchLabel} ${writeStatus.bsStyle === 'success' ? '✅' : '⚠️'} **${writeStatus.label}**`);
        if (write.stepName) {
          lines.push(`_Step: ${write.stepName}${write.stepType ? ` (${write.stepType})` : ''}_`);
        }
        lines.push('');
        lines.push('```diff');
        const expected = typeof write.expectedPreview !== 'undefined' ? write.expectedPreview : write.expected;
        const actual = typeof write.actualPreview !== 'undefined' ? write.actualPreview : write.actual;
        lines.push(createMarkdownDiff(expected, actual, `step_${writeIndex}.json`));
        lines.push('```');
        lines.push('');
      });
    });
  } else if (!effectiveSummaryMismatch && mismatchCount === 0 && !redactedGroups.some(group => group.hasMismatch)) {
    lines.push('_No diffs found._');
    lines.push('');
  } else if (!effectiveSummaryMismatch) {
    lines.push('## Diff Details Unavailable');
    lines.push('');
    lines.push('_The run reports mismatches, but no bounded write previews are available._');
    lines.push('');
  }

  return lines.join('\n');
}
